import { Button } from '@/components/Button/Button';
import { Checkbox } from '@/components/Checkbox/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { Input } from '@/components/Input';
import { PromoCodeFooterLabel } from '@/components/PromoCodeFooterLabel/PromoCodeFooterLabel';
import {
  CalendarIcon,
  ChevronDownIcon,
  ChevronLeftIcon,
  ClockIcon,
  CrossIcon,
  LocationIcon,
  PeopleIcon,
  TrashIcon,
  WarningIcon,
} from '@/components/icons';
import { useBookingDetails, type SkierFormData, type SkierSlot } from '@/pages/useBookingDetails';
import { getCheckoutSession, ordersService, setCheckoutUrl } from '@/services/orders.service';
import { useBookSkiPassStore } from '@/store/book-ski-pass.store';
import { isValidBirthDate, isValidEmail, isValidPhone } from '@/utils/booking';
import { classNames } from '@/utils/classNames';
import { formatDateOnlyForDisplay } from '@/utils/date-only';
import {
  buildSkierTypesFromRestrictions,
  type DomainRestriction,
} from '@/utils/domain-restrictions';
import { resolvePromoDomainCoverage } from '@/utils/promo-pricing';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { BottomSheet } from '@/components/BottomSheet';
import { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import styles from './BookingDetailsPage.module.css';

function formatPrice(cents: number, currency: string): string {
  const value = (cents / 100).toFixed(2);
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: currency || 'EUR',
  }).format(Number(value));
}

function formatTripDate(isoDate: string): string {
  return formatDateOnlyForDisplay(isoDate, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function totalPeople(counts: { category: string; quantity: number }[]): number {
  return counts.reduce((s, c) => s + c.quantity, 0);
}

function buildSelectionsFromSkierSlots(
  slots: SkierSlot[]
) {
  const grouped = new Map<string, { domain: SkierSlot['domain']; ageCategory: string; passOfferId?: number; passTitle: string; quantity: number }>();

  for (let i = 0; i < slots.length; i++) {
    const slot = slots[i];
    if (!slot) continue;

    const key = [
      String(slot.domain.id),
      slot.ageCategory.trim().toLowerCase(),
      String(slot.passOfferId ?? ''),
      slot.passTitle.trim().toLowerCase(),
    ].join('|');

    const existing = grouped.get(key);
    if (existing) {
      existing.quantity += 1;
    } else {
      grouped.set(key, {
        domain: slot.domain,
        ageCategory: slot.ageCategory,
        passOfferId: slot.passOfferId,
        passTitle: slot.passTitle,
        quantity: 1,
      });
    }
  }

  return Array.from(grouped.values()).map((entry) => ({
    domain: entry.domain,
    ageCategory: entry.ageCategory,
    passOfferId: entry.passOfferId,
    passTitle: entry.passTitle,
    quantity: entry.quantity,
  }));
}

function getPassTitleOnly(domainName: string, passTitle: string): string {
  if (!passTitle) return '';
  let newTitle = passTitle;
  if (passTitle.toLowerCase().startsWith(domainName.toLowerCase())) {
    newTitle = passTitle.slice(domainName.length).trim() || passTitle;
  }
  const daysMatch = newTitle.match(/(\d+)[\s-]*day/i);
  if (daysMatch) {
    const daysCount = Number(daysMatch[1]);
    if (Number.isFinite(daysCount) && daysCount > 0) {
      newTitle = `${daysCount}-day`;
    }
  }
  return newTitle;
}

function useLearnMore() {
  const [hoverOpen, setHoverOpen] = useState(false);
  const [tapOpen, setTapOpen] = useState(false);
  const hasHover = useMediaQuery('(hover: hover)');
  return {
    isVisible: hoverOpen || tapOpen,
    onMouseEnter: () => {
      setHoverOpen(true);
    },
    onMouseLeave: () => {
      setHoverOpen(false);
    },
    onTap: useCallback(() => {
      if (hasHover) return;
      setTapOpen((p) => !p);
    }, [hasHover]),
    onClose: useCallback(() => {
      setHoverOpen(false);
      setTapOpen(false);
    }, []),
  };
}

const SUPPORT_EMAIL = 'mailto:info@alps2alps.com';

type SkierCardProps = {
  slot: SkierSlot;
  form: SkierFormData;
  onFormChange: (updates: Partial<SkierFormData>) => void;
  categoryChanged: boolean;
  invalidAge: boolean;
  restrictions: DomainRestriction[];
};

const BIRTH_DATE_MIN = new Date(1900, 0, 1);
const BIRTH_DATE_MAX = new Date();
const BIRTH_DATE_DEFAULT_VIEW = new Date(new Date().getFullYear() - 25, 0, 1);

function SkierCard({ slot, form, onFormChange, categoryChanged, invalidAge, restrictions }: SkierCardProps) {
  const skierCardRef = useRef<HTMLDetailsElement | null>(null);
  const expandScrollTimeoutRef = useRef<number | null>(null);
  const isComplete =
    form.firstName.trim().length > 0 &&
    form.lastName.trim().length > 0 &&
    isValidBirthDate(form.birthDate.trim()) &&
    !invalidAge;

  const hasName = form.firstName.trim() || form.lastName.trim();
  const displayName = hasName
    ? `${form.firstName.trim()} ${form.lastName.trim()}`.trim()
    : `Skier #${String(slot.index + 1)}`;

  const passTitleOnly = getPassTitleOnly(slot.domain.name, slot.passTitle);

  const learnMore = useLearnMore();
  const skierTypes = useMemo(() => buildSkierTypesFromRestrictions(restrictions), [restrictions]);

  const scrollToSkierCard = useCallback(() => {
    if (typeof window === 'undefined') return;
    const skierCard = skierCardRef.current;
    if (!skierCard) return;

    const isMobile = window.matchMedia('(max-width: 767px)').matches;
    const offset = isMobile ? 50 : 100;
    const top = skierCard.getBoundingClientRect().top + window.scrollY - offset;

    window.scrollTo({
      top: Math.max(0, top),
      behavior: 'smooth',
    });
  }, []);

  const handleToggle = useCallback(
    (e: React.SyntheticEvent<HTMLDetailsElement>) => {
      if (!e.currentTarget.open) return;

      if (expandScrollTimeoutRef.current != null) {
        window.clearTimeout(expandScrollTimeoutRef.current);
        expandScrollTimeoutRef.current = null;
      }

      requestAnimationFrame(() => {
        scrollToSkierCard();
      });

      expandScrollTimeoutRef.current = window.setTimeout(() => {
        scrollToSkierCard();
        expandScrollTimeoutRef.current = null;
      }, 180);
    },
    [scrollToSkierCard]
  );

  useEffect(() => {
    return () => {
      if (expandScrollTimeoutRef.current != null) {
        window.clearTimeout(expandScrollTimeoutRef.current);
      }
    };
  }, []);

  const metaText = [
    !invalidAge && slot.ageCategory,
    slot.domain.name,
    passTitleOnly || null,
  ]
    .filter(Boolean)
    .join(' • ');

  return (
    <details ref={skierCardRef} className={styles.skierCard} onToggle={handleToggle}>
      <summary className={styles.skierCardSummary}>
        <div className={styles.skierCardSummaryRow1}>
          <div className={styles.skierCardNameGroup}>
            <span className={styles.skierName}>{displayName}</span>
            <span
              className={classNames(
                styles.statusBadge,
                isComplete && styles.statusComplete,
                !isComplete && invalidAge && styles.statusUnavailable,
                !isComplete && !invalidAge && styles.statusIncomplete
              )}
            >
              {isComplete ? 'Completed' : invalidAge ? 'Unavailable' : 'Incompleted'}
            </span>
          </div>
          {metaText && (
            <div className={styles.skierCardSummaryRow2}>
              <span className={styles.skierCardMetaText}>{metaText}</span>
            </div>
          )}
          <ChevronDownIcon className={styles.skierCardChevron} aria-hidden />
        </div>
      </summary>
      <div className={styles.skierCardContent}>
        <div className={styles.skierForm}>
          <Input
            className={styles.skierInput}
            label="First name"
            required
            id={`skier-${String(slot.index)}-first`}
            type="text"
            value={form.firstName}
            onChange={(e) => {
              onFormChange({ firstName: e.target.value });
            }}
            placeholder="First name"
          />
          <Input
            className={styles.skierInput}
            label="Last name"
            required
            id={`skier-${String(slot.index)}-last`}
            type="text"
            value={form.lastName}
            onChange={(e) => {
              onFormChange({ lastName: e.target.value });
            }}
            placeholder="Last name"
          />
          <div className={styles.skierFormFieldBirth}>
            <label
              htmlFor={`skier-${String(slot.index)}-birth`}
              className={styles.skierBirthLabel}
            >
              Date of birth
              <span className={styles.skierBirthRequired} aria-hidden>
                {' '}
                *
              </span>
            </label>
            <DatePicker
              id={`skier-${String(slot.index)}-birth`}
              value={form.birthDate}
              onChange={(dateString) => {
                onFormChange({ birthDate: dateString });
              }}
              minDate={BIRTH_DATE_MIN}
              maxDate={BIRTH_DATE_MAX}
              defaultViewDate={BIRTH_DATE_DEFAULT_VIEW}
              sheetTitle="Date of birth"
            />
            {invalidAge && (
              <div className={styles.invalidAgeHint}>
                We can&apos;t find a valid skipass option for this age in this ski area. In many
                resorts this ticket may be free or issued directly at the ticket office.{' '}
                <a href={SUPPORT_EMAIL} className={styles.invalidAgeLink}>
                  Contact support
                </a>
              </div>
            )}
            {!invalidAge && categoryChanged && (
              <div className={styles.categoryHint}>
                Pass age category updated based on resort age rules.{' '}
                {skierTypes.length > 0 && (
                  <span
                    className={styles.learnMoreWrapper}
                    onMouseEnter={learnMore.onMouseEnter}
                    onMouseLeave={learnMore.onMouseLeave}
                  >
                    <button
                      type="button"
                      className={styles.learnMoreBtn}
                      onClick={learnMore.onTap}
                      aria-expanded={learnMore.isVisible}
                    >
                      Learn more
                    </button>
                    {learnMore.isVisible && (
                      <div className={styles.learnMorePopup} role="tooltip">
                        <div className={styles.learnMorePopupHeader}>
                          <span className={styles.learnMorePopupTitle}>
                            Age categories for {slot.domain.name}
                          </span>
                          <button
                            type="button"
                            className={styles.learnMoreClose}
                            onClick={learnMore.onClose}
                            aria-label="Close"
                          >
                            ×
                          </button>
                        </div>
                        <ul className={styles.learnMoreList}>
                          {skierTypes.map((t) => (
                            <li key={t.key} className={styles.learnMoreItem}>
                              <span className={styles.learnMoreCategory}>{t.label}</span>
                              {t.ageRange && (
                                <span className={styles.learnMoreRange}>{t.ageRange}</span>
                              )}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </details>
  );
}

type RemoveConfirmModalProps = {
  isOpen: boolean;
  slotDescription: string;
  onCancel: () => void;
  onConfirm: () => void;
};

function RemoveConfirmModal({
  isOpen,
  slotDescription,
  onCancel,
  onConfirm,
}: RemoveConfirmModalProps) {
  const isMobile = useMediaQuery('(max-width: 768px)');

  useEffect(() => {
    if (!isOpen || isMobile) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onCancel();
    };
    document.addEventListener('keydown', handler);
    return () => {
      document.removeEventListener('keydown', handler);
    };
  }, [isOpen, isMobile, onCancel]);

  if (!isOpen) return null;

  const description = `This will remove the ${slotDescription} ski pass from your booking.`;
  const actions = (
    <div className={styles.modalActions}>
      <Button variant="outline" size="large" className={styles.modalActionGrow} onClick={onCancel}>
        Cancel
      </Button>
      <Button variant="destructive" size="large" className={styles.modalActionGrow} onClick={onConfirm}>
        Remove skier
      </Button>
    </div>
  );

  if (isMobile) {
    return (
      <BottomSheet
        isOpen={isOpen}
        onClose={onCancel}
        title="Remove skier?"
        footer={actions}
      >
        <p className={styles.modalDescription}>{description}</p>
      </BottomSheet>
    );
  }

  return (
    <div className={styles.modalBackdrop} onClick={onCancel}>
      <div
        className={styles.modalContent}
        role="dialog"
        aria-modal="true"
        aria-labelledby="remove-skier-title"
        onClick={(e) => {
          e.stopPropagation();
        }}
      >
        <div className={styles.modalTop}>
          <div className={styles.modalTextGroup}>
            <h3 id="remove-skier-title" className={styles.modalTitle}>
              Remove skier?
            </h3>
            <p className={styles.modalDescription}>{description}</p>
          </div>
          <button
            type="button"
            className={styles.modalCloseBtn}
            onClick={onCancel}
            aria-label="Close"
          >
            <CrossIcon className={styles.modalCloseIcon} />
          </button>
        </div>
        {actions}
      </div>
    </div>
  );
}

export const BookingDetailsPage = () => {
  const navigate = useNavigate();
  const referenceResortDomains = useBookSkiPassStore((s) => s.referenceResortDomains);
  const selectedDomain = useBookSkiPassStore((s) => s.selectedDomain);
  const stepOnePromoCode = useBookSkiPassStore((s) => s.stepOneFormFieldsValues.promoCode);
  const updateStepThreeFormFields = useBookSkiPassStore((s) => s.updateStepThreeFormFields);

  const {
    checkout,
    loading,
    offersLoading,
    error,
    customerEmail,
    setCustomerEmail,
    customerPhone,
    setCustomerPhone,
    skierSlots,
    skierForms,
    updateStepThreeSkierForm,
    consent,
    setConsent,
    completedCount,
    canSubmit,
    buildOrderItems,
    grandTotal,
    subTotal,
    currency,
    changedCategories,
    domainRestrictionsMap,
    invalidAgeBySlotIndex,
    removeSkierSlot,
    promoCode,
    promoCodeInfo,
    skierDetailsReadyForPayment,
  } = useBookingDetails();
  const trip = checkout?.tripDetails;
  const startLabel = trip?.startDate ? formatTripDate(trip.startDate) : '–';
  const durationLabel = !trip?.skiDays ? '–' : trip.skiDays > 1 ? `${String(trip.skiDays)} days` : '1 day';
  const peopleCount = totalPeople(trip?.skierCounts ?? []);
  const groupLabel = peopleCount === 1 ? '1 person' : `${String(peopleCount)} people`;
  const areaLabel =
    referenceResortDomains?.map((d) => d.name).join(', ') ??
    (selectedDomain != null ? selectedDomain.title : (trip?.domain?.name ?? '–'));

  const consentFieldId = useId();
  const [floatingSkierAlert, setFloatingSkierAlert] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [phoneTouched, setPhoneTouched] = useState(false);
  const [submitAttempted, setSubmitAttempted] = useState(false);
  const [pendingRemoveIndex, setPendingRemoveIndex] = useState<number | null>(null);

  const trimmedEmail = customerEmail.trim();
  const trimmedPhone = customerPhone.trim();

  const emailError =
    trimmedEmail.length === 0
      ? 'Enter your email address.'
      : !isValidEmail(trimmedEmail)
        ? 'Enter a valid email address, for example name@example.com.'
        : null;

  const phoneError =
    trimmedPhone.length === 0
      ? 'Enter your phone number.'
      : !isValidPhone(trimmedPhone)
        ? 'Enter a valid phone number with country code, for example +44 7700 900123.'
        : null;

  const showEmailError = (emailTouched || submitAttempted) && emailError != null;
  const showPhoneError = (phoneTouched || submitAttempted) && phoneError != null;

  const hasValidCustomerContacts =
    trimmedEmail.length > 0 &&
    trimmedPhone.length > 0 &&
    emailError == null &&
    phoneError == null;

  const primaryContinueEnabled =
    hasValidCustomerContacts && consent && !offersLoading && !isSubmitting;

  const canContinue = canSubmit && emailError == null && phoneError == null && !offersLoading;

  const effectivePromoCodeInfo = useMemo(() => {
    if (!promoCodeInfo) return promoCodeInfo;

    const hasLiveDiscount = grandTotal !== subTotal;
    const validSkierDomainItems = skierSlots
      .filter((_, i) => !invalidAgeBySlotIndex[i])
      .map((slot) => ({ domainId: slot.domain.id }));
    const domainCoverage = resolvePromoDomainCoverage(validSkierDomainItems, promoCodeInfo);

    return {
      ...promoCodeInfo,
      status: hasLiveDiscount ? ('active' as const) : ('inactive' as const),
      inactiveReason: hasLiveDiscount ? undefined : promoCodeInfo.inactiveReason,
      domainRestrictionPartial: domainCoverage.domainRestrictionPartial,
      domainRestrictionInvalid: domainCoverage.domainRestrictionInvalid,
    };
  }, [promoCodeInfo, grandTotal, subTotal, skierSlots, invalidAgeBySlotIndex]);

  useEffect(() => {
    if (skierDetailsReadyForPayment) setFloatingSkierAlert(false);
  }, [skierDetailsReadyForPayment]);

  const handleContinue = useCallback(() => {
    setSubmitAttempted(true);

    if (skierSlots.length > 0 && !skierDetailsReadyForPayment) {
      setFloatingSkierAlert(true);
      return;
    }

    const session = getCheckoutSession();
    if (!session || !canContinue) return;

    const submit = async () => {
      setSubmitError(null);
      setIsSubmitting(true);
      const items = buildOrderItems();
      const selections = buildSelectionsFromSkierSlots(skierSlots);
      try {
        const updatedCheckout = await ordersService.updateDetails(
          session.orderId,
          session.checkoutToken,
          {
            customerEmail: customerEmail.trim(),
            customerPhone: customerPhone.trim(),
            items,
          }
        );

        // Keep pass selections authoritative after details update, since details payload may
        // re-shape order items and backend can recalculate pass selections.
        await ordersService.updatePasses(session.orderId, session.checkoutToken, {
          selections,
        });

        if (Array.isArray(updatedCheckout.items) && updatedCheckout.items.length > 0) {
          updateStepThreeFormFields({
            passSelectionOverrides: selections,
            skierForms: updatedCheckout.items.map((item) => ({
              skipassId: item.skipassId,
              firstName: item.firstName,
              lastName: item.lastName,
              birthDate: item.birthDate ? item.birthDate.slice(0, 10) : '',
            })),
          });
        }

        if (updatedCheckout.checkoutUrl) {
          setCheckoutUrl(updatedCheckout.checkoutUrl);
        }
        //console.log('Updated checkout after saving details', updatedCheckout);
        //setIsSubmitting(false);
        void navigate('/payment');
      } catch (err) {
        setSubmitError(err instanceof Error ? err.message : 'Failed to save booking details');
      } finally {
        setIsSubmitting(false);
      }
    };

    void submit();
  }, [
    canContinue,
    buildOrderItems,
    customerEmail,
    customerPhone,
    navigate,
    updateStepThreeFormFields,
    skierSlots,
    skierSlots.length,
    skierDetailsReadyForPayment,
  ]);

  if (loading && !checkout) {
    return (
      <div className={styles.content}>
        <p className={styles.message}>Loading booking details…</p>
      </div>
    );
  }

  if (!checkout && !getCheckoutSession()) {
    return <Navigate to="/trip-details" replace />;
  }

  if (error) {
    return (
      <div className={styles.content}>
        <p className={styles.error}>{error}</p>
      </div>
    );
  }

  if (!checkout) {
    return (
      <div className={styles.content}>
        <p className={styles.message}>No checkout session. Please start from trip details.</p>
      </div>
    );
  }

  const pendingSlot = pendingRemoveIndex != null ? skierSlots[pendingRemoveIndex] : null;
  const pendingSlotDescription = pendingSlot
    ? [
        pendingSlot.ageCategory,
        pendingSlot.domain.name,
        getPassTitleOnly(pendingSlot.domain.name, pendingSlot.passTitle) || pendingSlot.passTitle,
      ]
        .filter(Boolean)
        .join(' · ')
    : '';

  return (
    <div className={styles.pageWrapper}>
      <div className={styles.content}>
        <div className={styles.tripStrip}>
          <div className={styles.tripStripMobileTop}>
            <span className={styles.tripStripArea}>{areaLabel}</span>
            <Link to="/trip-details" className={styles.editTripLink}>
              <ChevronLeftIcon className={styles.editTripChevron} aria-hidden />
              Edit trip details
            </Link>
          </div>
          <div className={styles.tripStripMobileBottom}>
            <span className={styles.tripStripMobileMeta}>{startLabel}</span>
            <span className={styles.tripStripMobileDivider} aria-hidden />
            <span className={styles.tripStripMobileMeta}>{durationLabel}</span>
            <span className={styles.tripStripMobileDivider} aria-hidden />
            <span className={styles.tripStripMobileMeta}>{groupLabel}</span>
          </div>
          <div className={styles.tripStripDesktop}>
            <div className={styles.tripStripLeft}>
              <span className={styles.tripStripItem}>
                <LocationIcon className={styles.tripStripIcon} aria-hidden />
                <span className={styles.tripStripLabel}>Going to: </span>
                <span className={styles.tripStripValue}>{areaLabel}</span>
              </span>
              <span className={styles.tripStripItem}>
                <CalendarIcon className={styles.tripStripIcon} aria-hidden />
                <span className={styles.tripStripLabel}>Start: </span>
                <span className={styles.tripStripValue}>{startLabel}</span>
              </span>
              <span className={styles.tripStripItem}>
                <ClockIcon className={styles.tripStripIcon} aria-hidden />
                <span className={styles.tripStripLabel}>Duration: </span>
                <span className={styles.tripStripValue}>{durationLabel}</span>
              </span>
              <span className={styles.tripStripItem}>
                <PeopleIcon className={styles.tripStripIcon} aria-hidden />
                <span className={styles.tripStripLabel}>Group: </span>
                <span className={styles.tripStripValue}>{groupLabel}</span>
              </span>
            </div>
            <Link to="/trip-details" className={styles.editTripLinkDesktop}>
              <ChevronLeftIcon className={styles.editTripChevron} aria-hidden />
              Edit trip details
            </Link>
          </div>
        </div>

        <div className={styles.bookingDetailsBlock}>
          <h4 className={styles.bookingDetailsTitle}>Booking details</h4>
          <p className={styles.bookingDetailsSubtitle}>
            We&apos;ll use these details to issue your ski passes and send booking updates
          </p>

          <div className={styles.bookingDetailsForm}>
            <Input
              className={styles.bookingField}
              label="Email"
              required
              id="booking-email"
              type="email"
              value={customerEmail}
              onChange={(e) => {
                setCustomerEmail(e.target.value);
              }}
              onBlur={() => {
                setEmailTouched(true);
              }}
              placeholder="your@email.com"
              error={showEmailError ? (emailError ?? undefined) : undefined}
            />
            <Input
              className={styles.bookingField}
              label="Phone number"
              required
              id="booking-phone"
              type="tel"
              value={customerPhone}
              onChange={(e) => {
                setCustomerPhone(e.target.value);
              }}
              onBlur={() => {
                setPhoneTouched(true);
              }}
              placeholder="+1 (555) 123-4567"
              error={showPhoneError ? (phoneError ?? undefined) : undefined}
            />
          </div>
        </div>

        <div className={styles.sectionBlock}>
          <h5 className={styles.sectionTitle}>Skier details</h5>
          <div className={styles.skierSectionRow}>
            <p className={styles.sectionSubtitle}>Complete all skiers to continue to payment</p>
            {skierSlots.length > 0 && (
              <span className={styles.completedBadge}>
                Completed: <span className={styles.completedBadgeValue}>{completedCount} / {skierSlots.length} skiers</span>
              </span>
            )}
          </div>

          <div className={styles.skierList}>
            {skierSlots.map((slot, i) => (
              <div key={slot.index} className={styles.skierRow}>
                <SkierCard
                  slot={slot}
                  form={skierForms[i] ?? { firstName: '', lastName: '', birthDate: '' }}
                  onFormChange={(updates) => {
                    updateStepThreeSkierForm(i, updates);
                  }}
                  categoryChanged={changedCategories[i] ?? false}
                  invalidAge={invalidAgeBySlotIndex[i] ?? false}
                  restrictions={domainRestrictionsMap.get(slot.domain.id) ?? []}
                />
                {skierSlots.length > 1 && (
                  <button
                    type="button"
                    className={styles.removeBtn}
                    onClick={() => {
                      setPendingRemoveIndex(i);
                    }}
                    aria-label={`Remove skier ${String(i + 1)}`}
                  >
                    <TrashIcon className={styles.removeBtnIcon} />
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>

        {skierSlots.length > 0 && (
          <footer className={styles.stickyFooter}>
            <div className={styles.footerInner}>
              <div className={styles.footerLeft}>
                <Checkbox
                  id={consentFieldId}
                  checked={consent}
                  onChange={(e) => {
                    setConsent(e.target.checked);
                  }}
                  style={{ alignItems: 'flex-start' }}
                  label="I confirm that I have obtained consent from all other persons in my group to provide their personal data for the purpose of issuing ski passes"
                />
              </div>
              <div className={styles.footerRight}>
                <div className={styles.footerTotalContainer}>
                  <PromoCodeFooterLabel
                    promoCode={promoCode}
                    promoCodeInfo={effectivePromoCodeInfo}
                    pendingCode={stepOnePromoCode.trim() || undefined}
                  />
                  {subTotal !== grandTotal && (
                    <span className={styles.footerTotalOriginal}>
                      {formatPrice(subTotal, currency)}
                    </span>
                  )}
                  <div className={styles.footerTotal}>
                    <span className={styles.footerTotalAmount}>
                      {formatPrice(grandTotal, currency)}
                    </span>
                  </div>
                </div>
                <div className={styles.footerButtonBlock}>
                  {submitError && <p className={styles.footerError}>{submitError}</p>}
                  <Button
                    variant="solid"
                    size="large"
                    loading={isSubmitting}
                    disabled={!primaryContinueEnabled || offersLoading}
                    onClick={handleContinue}
                    className={styles.footerContinueBtn}
                  >
                    {offersLoading ? 'Updating price…' : 'Continue to payment'}
                  </Button>
                </div>
              </div>
            </div>
          </footer>
        )}
      </div>

      {floatingSkierAlert && (
        <div className={styles.floatingAlert} role="alert">
          <WarningIcon className={styles.floatingAlertIcon} aria-hidden />
          <span className={styles.floatingAlertText}>
            Please provide at least one skier details
          </span>
        </div>
      )}
      <RemoveConfirmModal
        isOpen={pendingRemoveIndex != null}
        slotDescription={pendingSlotDescription}
        onCancel={() => {
          setPendingRemoveIndex(null);
        }}
        onConfirm={() => {
          if (pendingRemoveIndex != null) removeSkierSlot(pendingRemoveIndex);
          setPendingRemoveIndex(null);
        }}
      />
    </div>
  );
};
